<template>
    <ListItem class="no-padding" @click="$emit('click', trip)">
        <div
            class="l-stack l-spread l-full-width l-gap-4 timeline"
            :style="'--color-trace: ' + traceColor"
        >
            <div class="l-stack l-gap-1">
                <div>
                    <div class="timline-bubble" />
                    <div class="l-inline l-start l-spread l-gap-2">
                        <div class="l-inline l-center-v l-gap-1">
                            <CalendarIcon height="18" width="18" />
                            <div>
                                {{
                                    formatDate(trip.first_measurement.timestamp)
                                }}
                            </div>
                            <div style="font-weight: bold">
                                {{
                                    formatTime(trip.first_measurement.timestamp)
                                }}
                            </div>
                        </div>
                        <span>
                            {{
                                formatTripDuration(
                                    trip.first_measurement.timestamp,
                                    trip.last_measurement.timestamp
                                )
                            }}
                        </span>
                    </div>
                </div>
                <div
                    v-if="hasTachographAccess && trip.driver_id"
                    class="l-inline l-center-v l-gap-1"
                >
                    <div>
                        <PersonIcon height="18" width="18" />
                    </div>
                    <p class="t-small">
                        {{ trip.driver_name }} ({{ trip.driver_id }})
                    </p>
                </div>
                <div class="l-inline l-start l-spread l-gap-2">
                    <div class="l-inline l-center-v l-gap-1">
                        <div>
                            <PinLocationIcon height="18" width="18" />
                        </div>
                        <p>
                            <span class="t-small">{{
                                trip.start_address
                            }}</span>
                        </p>
                    </div>
                    <span v-if="trip.trip_distance">
                        {{ formatDistance(trip.trip_distance) }}
                    </span>
                </div>
                <div v-if="isTrailer" class="l-inline l-center-v l-gap-1">
                    <div>
                        <TruckIcon height="18" width="18" />
                    </div>
                    <p class="t-small">
                        {{ tracker.asset_details.name }}
                    </p>
                </div>
                <div
                    v-else-if="trip.trailer_detection_manually_deactivated"
                    class="l-inline l-center-v l-gap-1"
                >
                    <div>
                        <TrailerIcon height="18" width="18" />
                    </div>
                    <p class="t-small">{{ $t('manuallyDeactivated') }}</p>
                </div>
                <div
                    v-else-if="!trip.triggered_outside_trailerfree_geofence"
                    class="l-inline l-center-v l-gap-1"
                >
                    <div>
                        <TrailerIcon height="18" width="18" />
                    </div>
                    <p class="t-small">{{ $t('trailerFreeZone') }}</p>
                </div>
                <div
                    v-else-if="trip.triptrailer && trip.triptrailer.name"
                    class="l-inline l-center-v l-gap-1"
                >
                    <div>
                        <TrailerIcon height="18" width="18" />
                    </div>
                    <p class="t-small">
                        {{ trip.triptrailer.name }}
                        ({{ trip.triptrailer.percentage }}%)
                    </p>
                </div>
                <div class="l-inline l-center-v l-gap-1">
                    <div>
                        <LocationTargetIcon
                            v-if="trip.has_gps_fix"
                            class="block"
                        />
                        <LocationTargetOffIcon v-else class="block negative" />
                    </div>
                    <p class="t-small">
                        {{
                            trip.has_gps_fix ? $t('hasGpsFix') : $t('noGpsFix')
                        }}
                    </p>
                </div>
            </div>
            <div class="l-stack l-gap-1">
                <div>
                    <div class="timline-bubble">
                        <span v-if="isTripOngoing">?</span>
                    </div>

                    <div
                        v-if="!isTripOngoing"
                        class="l-inline l-center-v l-gap-1"
                    >
                        <CalendarIcon height="18" width="18" />
                        <div>
                            {{ formatDate(trip.last_measurement.timestamp) }}
                        </div>
                        <div>
                            <strong>
                                {{
                                    formatTime(trip.last_measurement.timestamp)
                                }}
                            </strong>
                        </div>
                    </div>
                </div>
                <div class="l-inline l-start l-gap-1">
                    <div v-if="!isTripOngoing">
                        <PinLocationIcon height="18" width="18" />
                    </div>
                    <p>
                        <span class="t-small">{{ trip.end_address }}</span>
                    </p>
                </div>
            </div>
        </div>
    </ListItem>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment-timezone'

import CalendarIcon from '@/components/icons/CalendarIcon'
import ListItem from '@/components/ListItem'
import LocationTargetIcon from '@/components/icons/LocationTargetIcon'
import LocationTargetOffIcon from '@/components/icons/LocationTargetOffIcon'
import PersonIcon from '@/components/icons/PersonIcon'
import PinLocationIcon from '@/components/icons/PinLocationIcon'
import TrailerIcon from '@/components/icons/TrailerIcon'
import TruckIcon from '@/components/icons/TruckIcon'

const HIGHLIGHTED_TRACE_COLOR = '#ef2655'

export default {
    name: 'TripListItem',
    components: {
        CalendarIcon,
        ListItem,
        LocationTargetIcon,
        LocationTargetOffIcon,
        PersonIcon,
        PinLocationIcon,
        TrailerIcon,
        TruckIcon,
    },
    props: {
        trip: {
            type: Object,
            required: true,
        },
        selected: {
            type: Boolean,
            default: false,
        },
        isTrailer: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters('auth', ['hasTachographAccess']),
        ...mapGetters('tracker', ['assetsByAssetId']),
        tracker() {
            return this.assetsByAssetId[this.trip.asset] || {}
        },
        traceColor() {
            if (this.selected) {
                return HIGHLIGHTED_TRACE_COLOR
            }
            return '#000000'
        },
        isTripOngoing() {
            return this.trip.status === 'running'
        },
    },
    methods: {
        formatDate(time) {
            return moment(time).format('DD.MM.YY')
        },
        formatDistance(distance) {
            return distance >= 1000
                ? `${(distance / 1000).toFixed(2)}km`
                : `${distance}m`
        },
        formatTime(time) {
            return moment(time).format('HH:mm')
        },
        formatTripDuration(startDate, endDate) {
            const timeDiff = moment(endDate).diff(moment(startDate))
            const tripDuration = moment.duration(timeDiff)
            return tripDuration.format('h[h] mm[min] ss[s]', {
                usePlural: false,
            })
        },
    },
}
</script>

<i18n>
{
    "en": {
        "hasGpsFix": "GPS fix",
        "manuallyDeactivated": "Manually deactivated",
        "trailerFreeZone": "Trailer-free zone",
        "noGpsFix": "No GPS fix"
    },
    "de": {
        "hasGpsFix": "GPS-Fix",
        "manuallyDeactivated": "Manuell deaktiviert",
        "trailerFreeZone": "Anhängerfreie Zone",
        "noGpsFix": "Kein GPS-Fix"
    },
    "fr": {
        "hasGpsFix": "Fix GPS",
        "manuallyDeactivated": "Désactivé manuellement",
        "trailerFreeZone": "Zone sans remorque",
        "noGpsFix": "Aucun fix GPS"
    },
    "it": {
        "hasGpsFix": "Fix GPS",
        "manuallyDeactivated": "Disattivato manualmente",
        "trailerFreeZone": "Zona senza rimorchio",
        "noGpsFix": "Nessun fix GPS"
    }
}
</i18n>

<style lang="scss" scoped>
.negative {
    color: $color-red;
}

.timeline {
    position: relative;
    border-left: 2px solid var(--color-trace);
    margin: 0 16px;
    padding: 16px;
}

.timline-bubble {
    --bubble_size: 18px;

    display: grid;
    justify-content: center;
    align-content: center;
    font-weight: bold;
    position: absolute;
    left: calc(((var(--bubble_size) / 2) + 1px) * -1);
    background-color: white;
    border: 2px solid var(--color-trace);
    border-radius: 50%;
    width: var(--bubble_size);
    height: var(--bubble_size);
}
</style>
